import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLang } from '../context/LanguageContext';
import { CalendarCheck, Package, Video } from 'lucide-react';

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-orange-100 flex items-center gap-4">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white">
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-sm text-stone-500">{label}</div>
      </div>
    </div>
  );
}

export default function UserDashboard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const { bookings } = useData();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false || (user && user.role !== 'user')) navigate('/login');
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 rounded-3xl p-8 text-white shadow-lg mb-8">
        <h1 className="text-3xl font-bold">{lang === 'en' ? 'Namaste' : 'नमस्ते'}, {user.name} 🙏</h1>
        <p className="opacity-90 mt-1">{lang === 'en' ? 'Your devotional journey' : 'आपकी भक्ति यात्रा'}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <StatCard icon={CalendarCheck} label={lang === 'en' ? 'Bookings' : 'बुकिंग'} value={bookings.length} />
        <StatCard icon={Package} label={lang === 'en' ? 'Prasad Received' : 'प्रसाद प्राप्त'} value={0} />
        <StatCard icon={Video} label={lang === 'en' ? 'Puja Videos' : 'पूजा वीडियो'} value={0} />
      </div>

      <h2 className="text-xl font-bold mb-4">{lang === 'en' ? 'My Sankalps' : 'मेरे संकल्प'}</h2>
      {bookings.length === 0 ? (
        <div className="bg-white border border-orange-100 rounded-2xl p-10 text-center">
          <p className="text-stone-500 mb-4">{lang === 'en' ? 'No bookings yet. Start your devotional journey!' : 'अभी तक कोई बुकिंग नहीं। अपनी भक्ति यात्रा शुरू करें!'}</p>
          <Link to="/upcoming-pujas" className="btn-orange rounded-full px-6 py-3 font-semibold inline-block">
            {lang === 'en' ? 'Browse Pujas' : 'पूजा देखें'}
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} className="bg-white border border-orange-100 rounded-2xl p-5 flex items-center gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-stone-900">{b.pujaTitle}</h3>
                <p className="text-sm text-stone-500">Sankalp: {b.user.name} • Gotra: {b.user.gotra}</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-orange-700">₹ {b.amount}</div>
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{b.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
